import type { ApiResponse, ErrorCode, LoginRequest, LoginResponse } from '@crm/shared'
import useSWR from 'swr'
import { useAuthStore } from '../stores/auth.store'

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: ErrorCode | 'UNKNOWN_ERROR',
    message: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

type ErrorPayload = {
  error?: {
    code?: ErrorCode
    message?: string
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token
  const headers = new Headers(init?.headers)
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${API_BASE}${url}`, { ...init, headers })

  if (res.status === 401) {
    useAuthStore.getState().clearAuth()
  }

  if (!res.ok) {
    const err = await res.json().catch<ErrorPayload>(() => ({ error: { message: res.statusText } }))
    throw new ApiClientError(
      res.status,
      err.error?.code ?? 'UNKNOWN_ERROR',
      err.error?.message || err.error?.code || 'Request failed',
    )
  }

  return res.json()
}

export function useApi<T>(path: string | null) {
  return useSWR<T, ApiClientError>(path, request, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 2000,
  })
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path)
}

export async function apiPost<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiPut<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function apiDelete<TResponse>(path: string): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'DELETE',
  })
}

export async function login(
  username: string,
  password: string,
): Promise<ApiResponse<LoginResponse>> {
  const payload: LoginRequest = { username, password }
  return apiPost<ApiResponse<LoginResponse>, LoginRequest>('/auth/login', payload)
}
